import React, { useEffect, useState } from "react";
import "./AgentEmployeeList.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import Loading from "../../../loading/Loading";
import CloseIcon from "../../../../components/icons/CloseIcon";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import AgencyEmployeeFilter from "../filter/AgentEmployeeFilter";
import { CommonEnums } from "../../../../enums/common.enums";
import { AgentEmployeeApi } from "../../../../utils/api/AgentEmployeeApi";
import AgentEmployeeTable from "./AgentEmployeeTable";
import AgentEmployeeFilter from "../filter/AgentEmployeeFilter";

export interface IAgentEmployeeData {
  _id: string;
  agency_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  mobile_number?: string;
  mobile_country_code?: string;
  designation?: string;
  status?: string;
  profile_photo?: any;
  createdAt?: string;
  updatedAt?: string;
}

const initialFilterState = {
  status: "",
  startDate: "00-00-00",
  endDate: "00-00-00",
};

const AgentEmployeeList = () => {
  const [state, setState] = useState({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    limit: 20,
    listData: [] as IAgentEmployeeData[],
    search: "",
  });

  const [filterValues, setFilterValues] = useState(initialFilterState);

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateString;
  };

  const fetchData = async (
    page: number,
    search: string,
    filters = filterValues,
  ) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await AgentEmployeeApi.list({
        page,
        limit: state.limit,
        search,
        status: filters.status,
        startDate: filters.startDate === "00-00-00" ? "" : filters.startDate,
        endDate: filters.endDate === "00-00-00" ? "" : filters.endDate,
      });

      if (response.success && response.data?.success) {
        const {
          docs,
          totalPages,
          page: currentPage,
          totalDocs,
          limit,
        } = response.data.data;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          listData: docs || [],
          totalPage: totalPages || 1,
          pageNumber: currentPage || 1,
          totalDocs: totalDocs || 0,
          limit: limit || prev.limit,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          listData: [],
        }));
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        listData: [],
      }));
    }
  };

  useEffect(() => {
    fetchData(state.pageNumber, state.search);
  }, []);

  const handleApplyFilter = (newFilters: any) => {
    setFilterValues(newFilters);
    setState((prev) => ({ ...prev, pageNumber: 1 }));
    fetchData(1, state.search, newFilters);
  };

  const handleRemoveFilter = (updates: any) => {
    const newFilters = { ...filterValues, ...updates };
    handleApplyFilter(newFilters);
  };

  const handleClearFilters = () => {
    handleApplyFilter(initialFilterState);
  };

  const onChangeSearch = (e: any) => {
    setState((prev) => ({ ...prev, search: e.target.value }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1, state.search, filterValues);
    }, 300);

    return () => clearTimeout(timer);
  }, [state.search]);

  const onClickNext = () => {
    if (state.pageNumber < state.totalPage) {
      fetchData(state.pageNumber + 1, state.search, filterValues);
    }
  };

  const onClickPreview = () => {
    if (state.pageNumber > 1) {
      fetchData(state.pageNumber - 1, state.search, filterValues);
    }
  };

  const isFilterActive =
    filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00");

  const startRange = (state.pageNumber - 1) * state.limit + 1;
  const endRange = Math.min(state.pageNumber * state.limit, state.totalDocs);

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const fetchEmployees = () => {
    return fetchData(state.pageNumber, state.search, filterValues);
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="agency-employee-list-container">
          <div className="list-heading">
            <p>Agent employees</p>
            <span className="sub-heading">View and manage your employees</span>
          </div>

          <ListTopButtonsAndSearch
            add_btn_name="Add"
            add_btn_link="/agent-employee/create"
            reset_btn_link="/agent-employee"
            placeholder="Search by name or email"
            onChangeSearch={onChangeSearch}
            type={CommonEnums.pageTypes?.agent_employee?.key}
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>

              <div className="chips-wrapper">
                {filterValues.status && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div
                      className="close-btn"
                      onClick={() => handleRemoveFilter({ status: "" })}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div
                      className="close-btn"
                      onClick={() =>
                        handleRemoveFilter({
                          startDate: "00-00-00",
                          endDate: "00-00-00",
                        })
                      }
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state.isLoading ? (
            <div className="data-not-found">
              <Loading />
            </div>
          ) : state.listData.length === 0 ? (
            <div className="data-not-found">
              <p>List is Empty!</p>
            </div>
          ) : (
            <AgentEmployeeTable
              listData={state.listData}
              onRefresh={() => fetchData(state.pageNumber, state.search, filterValues)}
            />
          )}

          {!state.isLoading && state.listData.length > 0 && (
            <div className="pagination-wrapper">
              <div className="viewing-text">
                Viewing {state.totalDocs > 0 ? startRange : 0}-{endRange} of{" "}
                {state.totalDocs} results
              </div>

              <div className="custom-pagination">
                <button
                  className="page-btn"
                  disabled={state.pageNumber === 1}
                  onClick={onClickPreview}
                >
                  ‹
                </button>

                <button
                  className="page-btn"
                  disabled={state.pageNumber === state.totalPage}
                  onClick={onClickNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <AgentEmployeeFilter
            initialFilterValues={filterValues}
            onApply={handleApplyFilter}
          />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgentEmployeeList;
